import mqtt from 'mqtt'
import { useEffect, useRef, useState } from 'react'
import { brokerClientId, resolveCentralBroker } from '@/lib/mqttConfig'

/**
 * Subscribe to topics and expose the latest value per topic.
 *
 * Values accumulate into a ref and flush to state on a fixed interval rather than
 * per message. At the packaging line's message rate a `setState` per message would
 * re-render the page dozens of times a second; this is the same accumulate-then-
 * flush approach already proven in `useKnowledgeGraph`, extracted so pages don't
 * each reimplement their own MQTT client inline.
 */

export interface MqttTopicValue {
  /** Parsed `value` field when the payload is `{"value":…}`, else the raw string. */
  value: unknown
  /** Source timestamp from the payload, when present. */
  timestamp?: string
  /** Local receipt time, used to detect a stale feed. */
  receivedAt: number
  /**
   * The whole decoded payload object, when the payload was a JSON object.
   *
   * The tag branches carry only `value` and `timestamp`, so most consumers want
   * [value] and nothing else. The platform's computed outputs (PRD v0.3 §7.3) carry
   * siblings that matter - `window_s`, `constraint_asset`, `computed_at` - and reading
   * `value` alone would silently discard them.
   */
  payload?: Record<string, unknown>
}

export interface UseMqttTopicValuesOptions {
  /** Topic filter(s) to subscribe to. Pass a stable value or memoise it. */
  topics: string | string[]
  clientIdPrefix?: string
  /** Flush cadence in ms. Defaults to 1s, comfortably faster than the 5s feed. */
  flushIntervalMs?: number
  enabled?: boolean
}

export interface UseMqttTopicValuesResult {
  /** Latest value per topic. Replaced wholesale on each flush. */
  values: Record<string, MqttTopicValue>
  isConnected: boolean
  messageCount: number
  error: string | null
}

/** Pull `value`/`timestamp` out of the Edge-style payload, tolerating bare scalars. */
function parsePayload(raw: string): {
  value: unknown
  timestamp?: string
  payload?: Record<string, unknown>
} {
  try {
    const parsed = JSON.parse(raw)
    if (parsed !== null && typeof parsed === 'object') {
      const obj = parsed as Record<string, unknown>
      return {
        // A computed output with no `value` key (the constraint topic is one) keeps the
        // whole object as its value, which is what consumers of those topics expect.
        value: 'value' in obj ? obj.value : parsed,
        timestamp: typeof obj.timestamp === 'string' ? obj.timestamp : undefined,
        payload: obj,
      }
    }
    return { value: parsed }
  } catch {
    // Not JSON - a plain scalar payload is still perfectly valid MQTT.
    return { value: raw }
  }
}

export function useMqttTopicValues({
  topics,
  clientIdPrefix = 'stories_values',
  flushIntervalMs = 1000,
  enabled = true,
}: UseMqttTopicValuesOptions): UseMqttTopicValuesResult {
  const [values, setValues] = useState<Record<string, MqttTopicValue>>({})
  const [isConnected, setIsConnected] = useState(false)
  const [messageCount, setMessageCount] = useState(0)
  const [error, setError] = useState<string | null>(null)

  const bufferRef = useRef<Map<string, MqttTopicValue>>(new Map())
  const countRef = useRef(0)
  const dirtyRef = useRef(false)
  const mountedRef = useRef(true)

  // Join the filters so the effect re-runs on content change, not identity change.
  const topicKey = Array.isArray(topics) ? topics.join('\n') : topics

  useEffect(() => {
    mountedRef.current = true
    if (!enabled || !topicKey) {
      return
    }

    const broker = resolveCentralBroker()
    if (!broker.url) {
      setError('No MQTT broker configured')
      return
    }

    const client = mqtt.connect(broker.url, {
      keepalive: 20,
      clientId: brokerClientId(clientIdPrefix),
      username: broker.username,
      password: broker.password,
      reconnectPeriod: 4000,
    })

    client.on('connect', () => {
      if (!mountedRef.current) {
        return
      }
      setIsConnected(true)
      setError(null)
      client.subscribe(topicKey.split('\n'), { qos: 1 })
    })

    client.on('message', (topic, payload) => {
      const { value, timestamp, payload: decoded } = parsePayload(payload.toString())
      bufferRef.current.set(topic, { value, timestamp, receivedAt: Date.now(), payload: decoded })
      countRef.current += 1
      dirtyRef.current = true
    })

    client.on('close', () => {
      if (mountedRef.current) {
        setIsConnected(false)
      }
    })

    client.on('error', (err) => {
      if (mountedRef.current) {
        setError(err.message)
      }
    })

    const flush = setInterval(() => {
      if (!mountedRef.current || !dirtyRef.current) {
        return
      }
      dirtyRef.current = false
      setValues(Object.fromEntries(bufferRef.current))
      setMessageCount(countRef.current)
    }, flushIntervalMs)

    return () => {
      mountedRef.current = false
      clearInterval(flush)
      client.removeAllListeners()
      client.end(true)
    }
  }, [topicKey, clientIdPrefix, flushIntervalMs, enabled])

  return { values, isConnected, messageCount, error }
}
